import React from 'react';
import {Template} from '@chekin/core';
import {toQuotedFontFamily} from 'utils/branding/brandingTokens';
import {FontsData} from 'utils/types';

async function getFontUrl(font: FontsData) {
  try {
    const downloadLink = font?.font_file_download_link as string;
    const response = await fetch(downloadLink);
    const blob = await response.blob();

    return {
      src: URL.createObjectURL(blob),
      name: font?.font_name,
      format: font?.font_format,
    };
  } catch (e) {
    console.error(e);
    return {};
  }
}

const CSS_FONT_FORMATS = new Set([
  'woff2',
  'woff',
  'truetype',
  'opentype',
  'embedded-opentype',
  'svg',
]);

export async function processFonts(fontsData: FontsData[]) {
  try {
    const fontBlobUrls = await Promise.all(fontsData.map(getFontUrl));
    let fontFaces = '';
    fontBlobUrls.forEach(fontBlobUrl => {
      const src = fontsData.find(
        font => font?.font_name === fontBlobUrl.name,
      )?.font_file_download_link;

      if (!fontBlobUrl.name || !src) {
        return;
      }

      const format = fontBlobUrl.format?.toLowerCase();
      const formatHint =
        format && CSS_FONT_FORMATS.has(format) ? ` format('${format}')` : '';

      fontFaces += `
          @font-face {
            font-family: ${toQuotedFontFamily(fontBlobUrl.name)};
            src: url('${encodeURI(src)}')${formatHint};
          }
        `;
    });

    return fontFaces;
  } catch (error) {
    console.error('Error processing fonts:', error);
    return '';
  }
}

function injectFontsIntoDocument(fontFaces: string, document: Document) {
  const stylesElement = document.createElement('style');
  stylesElement.appendChild(document.createTextNode(fontFaces));
  document.getElementsByTagName('head')[0].appendChild(stylesElement);
}

type UseTemplateFontsProps = {
  onSuccess?: (data: {[key: string]: FontsData}) => void;
};

const defaultProps = {
  onSuccess: undefined,
};
function useTemplateFonts({onSuccess}: UseTemplateFontsProps = defaultProps) {
  const [fontsStatus, setFontsStatus] = React.useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const fetchFonts = React.useCallback(
    async (templateData: Template) => {
      const customFonts = [templateData?.titles_font, templateData?.body_font];
      setFontsStatus('loading');
      const builtFontFaces = await processFonts(customFonts);
      injectFontsIntoDocument(builtFontFaces, document);

      onSuccess?.({
        titles_font: templateData?.titles_font,
        body_font: templateData?.body_font,
      });
      setFontsStatus('success');
    },
    [onSuccess],
  );

  return {fetchFonts, fontsStatus};
}

export {useTemplateFonts};
